import type { ReactElement } from "react"
import styled from "styled-components"

/** Track and knob geometry, kept together so the knob's travel derives from the track. */
const TRACK_WIDTH = "2.125rem"
const TRACK_HEIGHT = "1.125rem"
const KNOB_SIZE = "0.875rem"
const KNOB_INSET = "0.125rem"

const Root = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textSecondary};
  white-space: nowrap;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`

const Track = styled.span`
  position: relative;
  flex: none;
  width: ${TRACK_WIDTH};
  height: ${TRACK_HEIGHT};
  border-radius: ${({ theme }) => theme.radius.pill};
  background-color: ${({ theme }) => theme.color.borderStrong};
  transition: background-color 120ms ease-in-out;

  &::after {
    content: "";
    position: absolute;
    top: ${KNOB_INSET};
    left: ${KNOB_INSET};
    width: ${KNOB_SIZE};
    height: ${KNOB_SIZE};
    border-radius: ${({ theme }) => theme.radius.pill};
    background-color: ${({ theme }) => theme.color.surface};
    transition: transform 120ms ease-in-out;
  }
`

/**
 * The real control, laid over the track rather than hidden off-screen, so the
 * browser's own focus ring and hit target land on the switch the user sees.
 * `appearance: none` leaves the checkbox with no painting of its own.
 */
const Input = styled.input`
  position: absolute;
  inset: 0;
  margin: 0;
  width: ${TRACK_WIDTH};
  height: ${TRACK_HEIGHT};
  appearance: none;
  border-radius: ${({ theme }) => theme.radius.pill};
  cursor: inherit;

  &:checked + ${Track} {
    background-color: ${({ theme }) => theme.color.accent};
  }

  &:checked + ${Track}::after {
    transform: translateX(calc(${TRACK_WIDTH} - ${KNOB_SIZE} - ${KNOB_INSET} - ${KNOB_INSET}));
  }

  &:focus-visible + ${Track} {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`

/** Holds the overlaid input against the track it paints. */
const Switch = styled.span`
  position: relative;
  display: inline-flex;
`

interface PocToggleProps {
  readonly id: string
  readonly label: string
  readonly checked: boolean
  readonly disabled?: boolean
  readonly onChange: (checked: boolean) => void
  readonly qaHook: string
}

/**
 * An on/off switch for the POC options panel.
 *
 * A checkbox underneath, because that is what it is — one independent setting,
 * not a choice between two named states — and because a native input keeps the
 * keyboard behaviour, the label association and the focus ring for free. Only
 * the painting is ours.
 */
export const PocToggle = ({ id, label, checked, disabled = false, onChange, qaHook }: PocToggleProps): ReactElement => (
  <Root htmlFor={id} $disabled={disabled}>
    <Switch>
      <Input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        data-qa-hook={qaHook}
        onChange={event => onChange(event.target.checked)}
      />
      <Track aria-hidden="true" />
    </Switch>
    {label}
  </Root>
)
